import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../ErrorMessage";
import { AuthLogin, EyeCloseIcon, EyeIcon, Tick } from "asset/icons";
import Input from "component/form/Input";
import { handleCheckEmail, handleSendVerificationEmail } from "api/fetchers";
import { LOGOUT_USER } from "store/action";
import { useDispatch, useSelector } from "react-redux";
import { trackOnboardingStep } from "utilities/googleAnalytics/trackOnboardingStep";
import Loaders from "component/loader";
import { toast } from "react-toastify";
import { Country } from "country-state-city";
import { handleFetchKybCountries } from "api/onboarding";
import PhoneCountryDropdown from "../onboarding/updateInformation/PhoneCountryDropdown";

const normalizeCountriesResponse = (responseBody) =>
    (Array.isArray(responseBody?.data) && responseBody.data) ||
    (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
    (Array.isArray(responseBody?.countries) && responseBody.countries) ||
    (Array.isArray(responseBody) && responseBody) ||
    [];

const normalizeLocalPhone = (value) =>
    String(value || "")
        .replace(/\D/g, "")
        .replace(/^0+/, "");

const SignUp = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selector = useSelector((state) => state.users);

    const [formData, setFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        password: "",
    });
    const [phoneCountryCode, setPhoneCountryCode] = useState("NG");
    const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const [passwordValidations, setPasswordValidations] = useState({
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        minLength: false,
    });

    const validatePassword = (password) => {
        setPasswordValidations({
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>\-_]/.test(password),
            minLength: password.length >= 8,
        });
    };

    const handleChange = (value, name) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === "password") validatePassword(value);
    };

    useEffect(() => {
        let ignore = false;

        const loadPhoneCountries = async () => {
            setCountriesLoading(true);
            try {
                const response = await handleFetchKybCountries();
                const supportedCountries = normalizeCountriesResponse(response);
                const source = supportedCountries.length
                    ? supportedCountries
                    : Country.getAllCountries();
                const options = source
                    .map((country) => {
                        const code = String(country.code || country.isoCode || "").toUpperCase();
                        const countryMeta = Country.getCountryByCode(code);
                        const phoneCode =
                            country.phoneCode || country.phonecode || countryMeta?.phonecode;

                        if (!code || !phoneCode) return null;

                        return {
                            code,
                            name: country.name || countryMeta?.name || code,
                            flag: country.flag || countryMeta?.flag || "",
                            dialCode: String(phoneCode).startsWith("+")
                                ? String(phoneCode)
                                : `+${phoneCode}`,
                        };
                    })
                    .filter(Boolean);

                if (!ignore) {
                    setPhoneCountryOptions(options);
                    if (!options.some((option) => option.code === "NG") && options[0]) {
                        setPhoneCountryCode(options[0].code);
                    }
                }
            } finally {
                if (!ignore) setCountriesLoading(false);
            }
        };

        loadPhoneCountries();
        return () => {
            ignore = true;
        };
    }, []);

    const selectedPhoneCountry = useMemo(
        () => phoneCountryOptions.find((option) => option.code === phoneCountryCode),
        [phoneCountryCode, phoneCountryOptions]
    );
    const localPhoneNumber = normalizeLocalPhone(formData.phoneNumber);
    const fullPhoneNumber = `${selectedPhoneCountry?.dialCode || ""}${localPhoneNumber}`;
    const phoneIsValid = /^\+\d{8,15}$/.test(fullPhoneNumber);

    // Helpers
    const getErrorMsg = (err) =>
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong. Please try again.";

    const fail = (msg) => {
        setErrorMessage(msg);
        toast.error(msg, { toastId: "signup-error" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { email, password, firstName, lastName } = formData;

        if (!email || !password || !firstName || !lastName || !formData.phoneNumber) {
            return fail("All fields are required.");
        }
        if (!phoneIsValid) return fail("Enter a valid phone number.");
        if (!Object.values(passwordValidations).every(Boolean)) {
            return fail("Password does not meet all the requirements.");
        }

        setLoading(true);
        setErrorMessage("");

        try {
            if (selector?.isLoggedIn) {
                LOGOUT_USER("", dispatch);
            }

            const res = await handleCheckEmail(email);
            if (res?.statusCode !== 200) {
                return fail(res?.message || "Unable to verify email. Please try again.");
            }

            await handleSendVerificationEmail(email, firstName, fullPhoneNumber);
            toast.success("Verification email sent. Check your inbox.");

            trackOnboardingStep("onboarding_start", email);

            navigate("/auth/verify-email", {
                state: {
                    email,
                    password,
                    firstName,
                    lastName,
                    phoneNumber: fullPhoneNumber,
                    flow: "signup",
                },
            });
        } catch (err) {
            fail(getErrorMsg(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-[500px] xl:max-w-[650px] !py-4 mt-10 lg:mt-0 md:mt-0 !px-3 bg-white rounded-2xl border border-[#EBEDF9]">
            <AuthLogin className="h-24 w-24" />

            <div className="md:px-6 pb-8">
                <div className="mb-6">
                    <h2 className="text-3xl font-semibold text-[#101828]">Get started today</h2>
                    <p className="text-[#667085] text-sm">
                        Create an account or{" "}
                        <span
                            onClick={() => navigate("/auth/signin")}
                            className="text-[#6f00ff] font-medium cursor-pointer"
                        >
                            Sign In
                        </span>
                    </p>
                </div>
                {errorMessage && <ErrorMessage message={errorMessage} />}
                <form onSubmit={handleSubmit} className="space-y-4 w-full">
                    <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(value) => handleChange(value, "email")}
                        placeholder="Enter your email"
                    />
                    <Input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(value) => handleChange(value, "firstName")}
                        placeholder="Enter your first name"
                    />
                    <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(value) => handleChange(value, "lastName")}
                        placeholder="Enter your last name"
                    />

                    <div className="flex h-[56px] w-full items-center gap-2">
                        <PhoneCountryDropdown
                            value={phoneCountryCode}
                            options={phoneCountryOptions}
                            onChange={setPhoneCountryCode}
                            loading={countriesLoading}
                        />
                        <div className="relative min-w-0 flex-1">
                            <span className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-sm text-[#667085]">
                                {selectedPhoneCountry?.dialCode || ""}
                            </span>
                            <Input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={(value) =>
                                    handleChange(value.replace(/\D/g, ""), "phoneNumber")
                                }
                                inputMode="tel"
                                maxLength={15}
                                placeholder="905 555 0123"
                                className="!mt-0 !pl-16"
                            />
                        </div>
                    </div>

                    <div className="relative">
                        <Input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={(value) => handleChange(value, "password")}
                            placeholder="Enter your password"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-3 flex items-center text-[#667085]"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? (
                                <EyeCloseIcon className="w-6 h-6" />
                            ) : (
                                <EyeIcon className="w-6 h-6" />
                            )}
                        </button>
                    </div>

                    {/* Password rules */}
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(passwordValidations).map(([key, valid]) => {
                            const labelMap = {
                                uppercase: "Uppercase",
                                lowercase: "Lowercase",
                                number: "Number",
                                specialChar: "Special character",
                                minLength: "8 characters",
                            };
                            return (
                                <span
                                    key={key}
                                    className={`px-2 py-1 text-xs flex rounded-full ${
                                        valid
                                            ? "bg-[#DCFDDB] text-[#1DCB1C] border border-[#1DCB1C]"
                                            : "bg-[#EFF0FB] text-[#475467]"
                                    }`}
                                >
                                    {valid && <Tick className="mr-1" />} {labelMap[key]}
                                </span>
                            );
                        })}
                    </div>

                    <p className="text-sm text-[#667085] mt-2">
                        By creating an account, you agree to our{" "}
                        <span className="text-[#6f00ff] font-medium">Terms of Service</span> and{" "}
                        <span className="text-[#6f00ff] font-medium">Privacy Policy</span>
                    </p>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={
                                loading ||
                                !formData.email ||
                                !formData.firstName ||
                                !formData.lastName ||
                                !phoneIsValid ||
                                !formData.password ||
                                !Object.values(passwordValidations).every(Boolean)
                            }
                            className={`px-6 py-3 rounded-lg text-white text-sm font-semibold ${
                                loading ||
                                !formData.email ||
                                !formData.firstName ||
                                !formData.lastName ||
                                !phoneIsValid ||
                                !formData.password ||
                                !Object.values(passwordValidations).every(Boolean)
                                    ? "bg-[#E9D7FE] opacity-50 cursor-not-allowed"
                                    : "bg-[#6f00ff] hover:bg-[#6B46C1]"
                            }`}
                        >
                            {loading ? <Loaders /> : "Create account"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignUp;
